import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CataloguePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State for products from Firestore
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedOrigin, setSelectedOrigin] = useState('All');
  const [isMobile, setIsMobile] = useState(false);

  // Fetch products from Firestore
  useEffect(() => {
    console.log('🔍 CataloguePage: Starting to fetch products...');
    
    // DEBUG: First let's see ALL products in the collection
    const debugQuery = query(collection(db, 'catalogueProducts'));
    onSnapshot(debugQuery, (debugSnapshot) => {
      console.log('🔍 DEBUG: Total products in collection:', debugSnapshot.docs.length);
      debugSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log('🔍 DEBUG: Product', doc.id, 'status:', data.status, 'data:', data);
      });
    });
    
    // First try with orderBy
    let q = query(
      collection(db, 'catalogueProducts'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📦 CataloguePage: Received snapshot with', snapshot.docs.length, 'documents');
      
      const productsData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Product data:', { id: doc.id, ...data });
        return {
          id: doc.id,
          ...data,
          // Map category to match expected format
          category: data.category === 'beans' ? 'Beans' : 
                   data.category === 'drip-bags' ? 'Drip Bags' :
                   data.category
        };
      });
      
      console.log('✅ CataloguePage: Processed products:', productsData);
      setProducts(productsData);
      setLoading(false);
    }, (error) => {
      console.error('❌ CataloguePage: Error fetching products with orderBy:', error);
      
      // If orderBy fails, try without it
      if (error.code === 'failed-precondition' || error.code === 'invalid-argument') {
        console.log('🔄 CataloguePage: Retrying without orderBy...');
        
        const fallbackQuery = query(
          collection(db, 'catalogueProducts'),
          where('status', '==', 'active')
        );
        
        return onSnapshot(fallbackQuery, (snapshot) => {
          console.log('📦 CataloguePage: Fallback - Received snapshot with', snapshot.docs.length, 'documents');
          
          const productsData = snapshot.docs.map(doc => {
            const data = doc.data();
            console.log('📄 Fallback Product data:', { id: doc.id, ...data });
            return {
              id: doc.id,
              ...data,
              category: data.category === 'beans' ? 'Beans' : 
                       data.category === 'drip-bags' ? 'Drip Bags' :
                       data.category
            };
          });
          
          console.log('✅ CataloguePage: Fallback - Processed products:', productsData);
          setProducts(productsData);
          setLoading(false);
        }, (fallbackError) => {
          console.error('❌ CataloguePage: Fallback query also failed:', fallbackError);
          setLoading(false);
        });
      } else {
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Check if mobile
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Get unique categories and origins for filters
  const categories = ['All', ...new Set(products.map(coffee => coffee.category))];
  const origins = ['All', ...new Set(products.map(coffee => coffee.origin))];
  
  console.log('🏷️ Available categories:', categories);
  console.log('🌍 Available origins:', origins);
  console.log('📊 Total products for filtering:', products.length);


  // Filter products based on search and filters
  const filteredCoffees = products.filter(coffee => {
    // First check if the product has required data
    if (!coffee.name || !coffee.image || !coffee.origin || !coffee.notes) {
      console.log('⚠️ Filtering out incomplete product:', coffee);
      return false;
    }

    const matchesSearch = coffee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coffee.origin.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coffee.producer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coffee.notes.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'All' || coffee.category === selectedCategory;
    const matchesOrigin = selectedOrigin === 'All' || coffee.origin === selectedOrigin;
    
    return matchesSearch && matchesCategory && matchesOrigin;
  });

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Header />
        <main style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{
            textAlign: 'center',
            fontSize: '18px',
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }}>
            Loading catalogue...
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Header />
      
      {/* Main Content */}
      <main style={{ flex: 1, padding: isMobile ? '40px 20px' : '40px 30px' }}>
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto' 
        }}>
          {/* Page Title - Removed */}

          {/* Search and Filters */}
          <div style={{
            border: '1px solid #000',
            padding: isMobile ? '20px' : '30px',
            marginBottom: isMobile ? '40px' : '50px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
              gap: isMobile ? '20px' : '30px',
              alignItems: 'end'
            }}>
              {/* Search Bar */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: isMobile ? '8px' : '12px',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  color: '#000',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Search
                </label>
                <input
                  type="text"
                  placeholder="TYPE TO SEARCH..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 0' : '16px 0',
                    border: 'none',
                    borderBottom: '1px solid #000',
                    fontSize: isMobile ? '14px' : '16px',
                    background: 'transparent',
                    outline: 'none',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Category Filter */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: isMobile ? '8px' : '12px',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  color: '#000',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 0' : '16px 0',
                    border: 'none',
                    borderBottom: '1px solid #000',
                    fontSize: isMobile ? '14px' : '16px',
                    background: 'transparent',
                    cursor: 'pointer',
                    outline: 'none',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Origin Filter */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: isMobile ? '8px' : '12px',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  color: '#000',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Origin
                </label>
                <select
                  value={selectedOrigin}
                  onChange={(e) => setSelectedOrigin(e.target.value)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 0' : '16px 0',
                    border: 'none',
                    borderBottom: '1px solid #000',
                    fontSize: isMobile ? '14px' : '16px',
                    background: 'transparent',
                    cursor: 'pointer',
                    outline: 'none',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}
                >
                  {origins.map(origin => (
                    <option key={origin} value={origin}>
                      {origin}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Counter */}
            <div style={{
              marginTop: isMobile ? '20px' : '30px',
              paddingTop: isMobile ? '15px' : '15px',
              borderTop: '1px solid #000',
              fontSize: isMobile ? '10px' : '12px',
              color: '#000',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {filteredCoffees.length} of {products.length} products
            </div>
          </div>

          {/* Coffee Cards Grid - Responsive */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
            gap: '1px',
            background: '#000',
            padding: '1px'
          }}>
            {filteredCoffees.map((coffee, index) => {
              // Additional safety check
              if (!coffee || !coffee.id) return null;
              
              return (
                <div
                  key={coffee.id}
                  onClick={() => navigate(`/product/${coffee.name.toLowerCase().replace(/\s+/g, '-')}`)}
                  style={{
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                {/* Product Image */}
                <div style={{
                  aspectRatio: '1/1',
                  overflow: 'hidden',
                  borderBottom: '1px solid #000'
                }}>
                  <img
                    src={coffee.image}
                    alt={coffee.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>

                {/* Product Info */}
                <div style={{ padding: isMobile ? '20px' : '20px' }}>
                  <div style={{
                    fontSize: isMobile ? '8px' : '10px',
                    fontWeight: '600',
                    color: '#000',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: isMobile ? '6px' : '8px'
                  }}>
                    {coffee.category}
                  </div>

                  <h3 style={{
                    fontSize: isMobile ? '16px' : '16px',
                    fontWeight: '300',
                    color: '#000',
                    margin: isMobile ? '0 0 8px 0' : '0 0 8px 0',
                    lineHeight: '1.2',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>
                    {coffee.name}
                  </h3>
                  
                  <div style={{
                    fontSize: isMobile ? '10px' : '11px',
                    color: '#666',
                    margin: isMobile ? '0 0 15px 0' : '0 0 15px 0',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>
                    {coffee.origin} — {coffee.region}
                  </div>

                  <div style={{
                    fontSize: isMobile ? '10px' : '11px',
                    color: '#000',
                    margin: isMobile ? '0 0 15px 0' : '0 0 15px 0',
                    lineHeight: '1.6',
                    minHeight: isMobile ? '36px' : '40px'
                  }}>
                    {coffee.notes}
                  </div>

                  <div style={{
                    borderTop: '1px solid #000',
                    paddingTop: isMobile ? '15px' : '15px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: isMobile ? '8px' : '10px'
                    }}>
                      <span style={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600',
                        color: '#000',
                        letterSpacing: '1px'
                      }}>
                        QAR {coffee.price}
                      </span>
                    </div>
                    
                    {/* Weight Options */}
                    {coffee.weightOptions && coffee.weightOptions.length > 0 && (
                      <div style={{
                        fontSize: isMobile ? '9px' : '10px',
                        color: '#666',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        Available: {coffee.weightOptions.map(option => `${option.weight}${option.unit}`).join(', ')}
                      </div>
                    )}
                  </div>

                </div>
              </div>
              );
            })}
          </div>

          {/* No Results */}
          {filteredCoffees.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '60px 20px' : '80px 40px',
              border: '1px solid #000'
            }}>
              <h3 style={{
                fontSize: isMobile ? '18px' : '24px',
                fontWeight: '100',
                margin: isMobile ? '0 0 15px 0' : '0 0 20px 0',
                letterSpacing: isMobile ? '2px' : '4px',
                textTransform: 'uppercase'
              }}>
                No Results Found
              </h3>
              <p style={{
                fontSize: isMobile ? '10px' : '12px',
                margin: 0,
                letterSpacing: isMobile ? '1px' : '2px',
                textTransform: 'uppercase',
                color: '#666'
              }}>
                Adjust filters or search terms
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CataloguePage;